import React from "react";
import SanitizedHTML from 'react-sanitized-html';
import slugify from 'slugify';
import {truncateString} from "../../utils/string";
import { timestampHumanizeFromNow, secondsToDuration, durationToDuration } from "../../utils/date";
import playIcon from '../../assets/img/icon/play-icon-white.svg';
import { useTranslation } from "react-i18next";
import he from 'he'

/**
 * Where a result links to.
 *
 * `url` is absolute and always points at the Crossmap sub-site page -- that is
 * the contract of the unified search schema. Every vertical used to build this
 * itself and none of them agreed: news and blogs reassembled `scheme://url` (the
 * publisher), videos used `embed_link` (YouTube), and books built
 * `books.crossmap.com?p=<cm_post_id>` from a WordPress post id the crawler faked.
 * Blogs alone had a `cm_url` meaning "the Crossmap copy, if any" -- which is what
 * `url` now means everywhere, so the field is gone.
 */
const resultUrl = (item) => {
    const src = item._source;
    // Unified schema: `url` is absolute and already points at the Crossmap
    // sub-site page.
    if (src.url && /^https?:\/\//i.test(src.url)) return src.url;
    // A document the backfill has not reached yet still carries the old shape:
    // a SCHEMELESS url plus a separate `scheme`. Reassembling it here is what
    // keeps the two states from being visibly different during a re-index --
    // without this, the live site renders `https://https//blogs.crossmap.com/...`
    // for migrated docs (old markup, new data) or a relative link for
    // un-migrated ones (new markup, old data). Both are broken; this is neither.
    if (src.url) return `${src.scheme || 'https'}://${src.url}`;
    // Last resort, for the fields the unified schema replaced: cm_url was the
    // blogs-only "Crossmap copy", embed_link the videos-only YouTube link.
    return src.cm_url || src.embed_link || '';
};

/** Who published it originally. `source_url` is the attribution, not the target. */
const sourceLabel = (item) => item._source.site_name || item._source.domain || "";

/**
 * The highlighted snippet.
 *
 * Elasticsearch returns whichever of the highlighted fields matched, and which
 * one that is differs per vertical -- videos and books match on the body, news
 * and blogs on the excerpt. Falling back to the stored excerpt keeps a result
 * readable when nothing highlighted.
 */
const snippet = (item) => {
    const h = item.highlight || {};
    return (h.excerpt && h.excerpt[0])
        || (h.text_content && h.text_content[0])
        || item._source.excerpt
        || "";
};

const NewsResult = ({item})=>{
    return(
        <li>
            <div className="quiet fz13">
                <a className="link" href={resultUrl(item)}>
                    <span>{sourceLabel(item)}</span>
                </a>
            </div>
            <div className="media-box md">
                {item._source.image &&
                    <figure className="media right">
                        <a href={resultUrl(item)}>
                            <img src={item._source.image} width="439" height="328" alt="" />
                        </a>
                    </figure>
                }
              <div className="txt">
                <h2 className="search-result-title">
                    <a className="link" href={resultUrl(item)}>{truncateString(item._source.title, 70)}</a>
                </h2>
                <p className="quiet p">
                    <SanitizedHTML
                      allowedTags={['strong']}
                      html={snippet(item)}
                    />
                </p>
                <div className="fz12 quiet">
                    <time>{timestampHumanizeFromNow(item._source.published_at / 1000)}</time>
                </div>
              </div>
            </div>
        </li>
    )
}

export function NewsResults({items}) {
    return (
        <ul className="news-list list divider md no-bullet">
            {
                items.map((item, index) =>(
                    <NewsResult key={item._id} item={item}/>
                ))
            }
        </ul>
    );
}

const BlogsResult = ({item})=>{
    return(
        <li>
            <div className="quiet fz13">
                <a className="link" href={resultUrl(item)}>
                    <span>{sourceLabel(item)}</span>{
                        item._source && item._source.author_name ? (
                            <span> - <SanitizedHTML style={{ display: 'inline' }}
                              allowedTags={['strong']}
                              html={item.highlight && item.highlight.author_name && item.highlight.author_name[0] || item._source.author_name}
                            /></span>
                        ) : ('')
                    }
                </a>
            </div>
            <div className="media-box md">
                {item._source.image &&
                    <figure className="media right">
                        <a href={resultUrl(item)}>
                            <img src={item._source.image} width="439" height="328" alt="" />
                        </a>
                    </figure>
                }
              <div className="txt">
                <h2 className="search-result-title">
                    <a className="link" href={resultUrl(item)}>{truncateString(item._source.title, 70)}</a>
                </h2>
                <p className="quiet p">
                    <SanitizedHTML
                      allowedTags={['strong']}
                      html={snippet(item)}
                    />
                </p>
                <div className="fz12 quiet">
                    <time>{timestampHumanizeFromNow(item._source.published_at / 1000)}</time>
                </div>
              </div>
            </div>
        </li>
    )
}

export function BlogsResults({items}) {
    return (
        <ul className="news-list list divider md no-bullet">
            {
                items.map((item, index) =>(
                    <BlogsResult key={item._id} item={item}/>
                ))
            }
        </ul>
    );
}
const PodcastResult = ({item})=>{
    const epUrl = `https://podcasts.crossmap.com/episodes/${slugify(item.title)}/${item.id}`;
    const chUrl = `https://podcasts.crossmap.com/channels/${slugify(item.channel.title)}/${item.channel.id}`

    return(
        <li>
          <figure className="round-sm has-decor"><a href={epUrl}><img src={item.image ? item.image.base_url._240x240 : item.channel.image ? item.channel.image.base_url._240x240 : null} width="385" height="385" alt="" />
            <time className="decor location-br txt"><span>{secondsToDuration(item.duration)}</span></time>
          </a></figure>
          <h2 className="fz16 fwn spacing-sm"><a className="link" href={epUrl}>{item.title}</a></h2>
          <div className="quiet fz12">
            <a className="link mr-xs" href={chUrl}>{item.channel.title}</a>
            <time>{timestampHumanizeFromNow(item.published_at)}</time>
          </div>
        </li>
    )
}

export function PodcastResults({items}) {
    return (
        <div className="episode-list-wp">
          <ul className="episode-list no-bullet">
            {
                items.map((item, index) =>(
                    <PodcastResult key={item.id} item={item}/>
                ))
            }
          </ul>
        </div>
    );
}

const VideoResult = ({item})=>{
    const { t } = useTranslation();
    return(
        <li>
          <h2 className="search-result-title"><a className="link" href={resultUrl(item)}>{truncateString(item._source.title, 70)}</a></h2>
          <div className="media-box md">
            <figure className="media left has-decor"><a href={resultUrl(item)}><img src={item._source.image} width="160" height="90" alt=""></img>
              <span className="decor location-cc gap-re"><img src={playIcon} width="25" height="25" alt=""></img></span>
	      <time className="decor location-br gap-lg txt"><span>{durationToDuration(item._source.duration)}</span></time>
            </a></figure>
	    <div className="txt">
              <p className="quiet p">
	        <SanitizedHTML
                  allowedTags={['strong']}
                  html={truncateString(snippet(item), 400)}
                />
	      </p>
              <div className="fz12 quiet">
                <span>{t('item.uploaded_by')} &nbsp;
                  <a className="txt-primary link mr-sm" href={`https://youtube.com/channel/${item._source.channel.id}`}>{item._source.channel.title}</a>
                </span>
                <time className="mr-sm">{timestampHumanizeFromNow(item._source.published_at / 1000)}</time>
              </div>
            </div>
          </div>
        </li>
    )
}

export function VideoResults({items}) {
    return (
        <ul className="video-list list divider md no-bullet">
            {
                items.map((item, index) =>(
                    <VideoResult key={item._id} item={item}/>
                ))
            }
        </ul>
    );
}

const BookResult = ({item})=>{
    return(
        <li>
          <div className="media-box md">
	    {item._source.images.length > 0 &&
              <figure className="media left"><a href={resultUrl(item)}><img src={item._source.images[0].link} width="100" alt=""></img></a></figure>
	    }
            <div className="txt">
              <h2 className="search-result-title"><a className="link" href={resultUrl(item)}>{he.decode(item._source.title)}</a></h2>
              <div className="fz12">
              </div>
            </div>
            <div className="book-description spacing-t-sm quiet">
	      <p>
	        <SanitizedHTML
                  allowedTags={['strong']}
                  html={truncateString(snippet(item), 400)}
                />
	      </p>
            </div>
          </div>
        </li>
    )
}

export function BookResults({items}) {
    return (
        <ul className="book-list list divider md no-bullet">
            {
                items.map((item, index) =>(
                    <BookResult key={item._id} item={item}/>
                ))
            }
        </ul>
    );
}

